import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories, mainSiteUrl } from "@/data/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const navLinks = [
    { label: t("nav.home"), href: "/", internal: true },
    { label: t("nav.spreadsheet"), href: "#about", internal: false },
    { label: t("nav.categories"), href: "#categories", internal: false },
    { label: t("nav.guides"), href: "#guides", internal: false },
    { label: t("nav.faq"), href: "#faq", internal: false },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-background-300/70 bg-background-50">
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
            Basetao <span className="text-primary-600">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) =>
            link.internal ? (
              <Link
                key={link.href}
                to={link.href}
                className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition hover:bg-background-100 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition hover:bg-background-100 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            )
          )}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <Link
            to="/article/basetao-spreadsheet-guide"
            className="inline-flex items-center gap-1.5 rounded-md border border-primary-300 bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700 transition hover:bg-primary-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-book-2-line"></i>
            {t("nav.shoppingGuide")}
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            {t("nav.visitMain")}
            <i className="ri-arrow-right-up-line"></i>
          </a>
          <LanguageSwitcher />
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/60 text-foreground-900 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-300/70 bg-background-50 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              {t("nav.home")}
            </Link>
            <a
              href="#about"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-information-line"></i>
              {t("nav.spreadsheet")}
            </a>

            <button
              type="button"
              onClick={() => setCategoriesOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition hover:bg-background-100 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-2-line"></i>
                {t("nav.categories")}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${categoriesOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoriesOpen && (
              <div className="ml-3 flex flex-col gap-0.5 border-l border-background-200 pl-3">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                  >
                    <span>{t(cat.i18nKey)}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400"></i>
                  </a>
                ))}
              </div>
            )}

            <a
              href="#guides"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              {t("nav.guides")}
            </a>
            <a
              href="#faq"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-question-line"></i>
              {t("nav.faq")}
            </a>

            <Link
              to="/article/basetao-spreadsheet-guide"
              onClick={() => setMenuOpen(false)}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-700 cursor-pointer"
            >
              <i className="ri-book-2-line"></i>
              {t("nav.shoppingGuide")}
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 cursor-pointer"
            >
              {t("nav.visitMain")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}